import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Mail, Smartphone, AlertCircle, Send, Filter, TrendingUp } from "lucide-react";
import Layout from "../../components/Layout";
import apiClient from "../../services/apiClient";

const getIcon = (group: string) => {
  switch (group) {
    case "parent":
      return <Smartphone className="w-5 h-5" />;
    case "vendors":
    case "service_providers":
      return <Mail className="w-5 h-5" />;
    case "veterinarians":
      return <AlertCircle className="w-5 h-5" />;
    default:
      return <Bell className="w-5 h-5" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "delivered":
    case "sent":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
    case "pending":
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
    case "failed":
      return "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400";
  }
};

const getChannelColor = (channel: string) => {
  switch (channel?.toLowerCase()) {
    case "sms":
      return "bg-blue-500";
    case "email":
      return "bg-purple-500";
    case "push":
      return "bg-indigo-500";
    default:
      return "bg-gray-500";
  }
};

const NotificationManagement: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterChannel, setFilterChannel] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastNotificationRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const handleCreateNew = () => {
    navigate("/notifications/send");
  };

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `v1/resource/notificationLog?page=${page}&limit=10`;
      if (filterChannel !== "all") url += `&channel=${filterChannel}`;

      const response: any = await apiClient(url);
      const newNotifications = response?.data?.notification_logs || [];
      setNotifications((prev) => [...prev, ...newNotifications]);
      setHasMore(newNotifications.length > 0);
    } catch (err: any) {
      setError(err.message || "Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setNotifications([]);
    setPage(1);
  }, [filterChannel]);

  useEffect(() => {
    fetchNotifications();
  }, [page, filterChannel]);

  const uniqueNotifications = useMemo(() => {
    const map = new Map<string, any>();
    notifications.forEach((n) => {
      if (!map.has(n.message)) map.set(n.message, n);
    });
    return Array.from(map.values());
  }, [notifications]);

  const stats = useMemo(() => {
    const total = uniqueNotifications.length;
    const delivered = uniqueNotifications.filter(n => n.status?.toLowerCase() === "delivered" || n.status?.toLowerCase() === "sent").length;
    const pending = uniqueNotifications.filter(n => n.status?.toLowerCase() === "pending").length;
    return { total, delivered, pending };
  }, [uniqueNotifications]);

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 dark:from-gray-900 dark:via-gray-900 dark:to-indigo-950/20">
        <div className="p-6 max-w-7xl mx-auto">
          {/* Hero Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/30">
                <Bell className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                  Notification Center
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">Manage and monitor all communications</p>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sent</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.total}</p>
                  </div>
                  <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Delivered</p>
                    <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">{stats.delivered}</p>
                  </div>
                  <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                    <Send className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
                    <p className="text-3xl font-bold text-amber-600 dark:text-amber-400 mt-1">{stats.pending}</p>
                  </div>
                  <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="mb-6 flex flex-wrap justify-between items-center gap-4 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <button
              onClick={handleCreateNew}
              className="group relative px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold hover:from-indigo-700 hover:to-purple-700 shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40 transition-all duration-300 hover:-translate-y-0.5"
            >
              <span className="flex items-center gap-2">
                <Send className="w-4 h-4" />
                Create Notification
              </span>
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>

              {showFilters && (
                <select
                  value={filterChannel}
                  onChange={(e) => setFilterChannel(e.target.value)}
                  className="px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="all">All Channels</option>
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                  <option value="push">Push</option>
                </select>
              )}
            </div>
          </div>

          {/* Notifications List */}
          {error && (
            <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-red-200 dark:border-red-900">
              <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
              <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            {uniqueNotifications.map((n, idx) => {
              const lastItem = idx === uniqueNotifications.length - 1;
              return (
                <div
                  key={n.id}
                  ref={lastItem ? lastNotificationRef : undefined}
                  className="group bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-lg hover:border-indigo-200 dark:hover:border-indigo-800 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all duration-300 text-white">
                      {getIcon(n.recipient_type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-800 dark:text-gray-200 font-medium mb-3 leading-relaxed">
                        {n.message}
                      </p>

                      <div className="flex flex-wrap items-center gap-3">
                        {/* Channel Badge */}
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-sm">
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${getChannelColor(
                              n.channel
                            )} animate-pulse`}
                          ></span>
                          {n.channel?.toUpperCase()}
                        </span>

                        {/* Status Badge */}
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            n.status
                          )}`}
                        >
                          {n.status}
                        </span>

                        {/* Timestamp */}
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {new Date(n.created_at).toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>


          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          )}

          {!loading && uniqueNotifications.length === 0 && (
            <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="inline-flex p-6 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full mb-6 shadow-xl">
                <Bell className="h-12 w-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                No notifications yet
              </h3>
              <p className="mt-2 text-gray-600 dark:text-gray-400 text-base mb-6">
                Get started by creating your first notification
              </p>
              <button
                onClick={handleCreateNew}
                className="px-8 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold hover:from-indigo-700 hover:to-purple-700 shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40 transition-all duration-300 hover:-translate-y-0.5"
              >
                Create Notification
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default NotificationManagement;